import React from "react";
import { useRef } from "react";
import Footer from "../components/Footer/PrelaunchFooter/Footer";
import Navbar from "../components/Navbar/Navbar";

export default function RefundPolicy() {
  const aboutUsRef = useRef(null);
  const ourProcessRef = useRef(null);
  const manufactureRef = useRef(null);
  const ingredientsRef = useRef(null);

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <Navbar
          aboutUsRef={aboutUsRef}
          ourProcessRef={ourProcessRef}
          manufactureRef={manufactureRef}
          ingredientsRef={ingredientsRef}
        />
      </div>
      <div style={styles.contentContainer}>
        <h1 style={styles.title}>Returns, Refund and Cancellation Policy</h1>
        <p style={styles.updatedDate}>Last Updated: February 1st, 2025</p>

        {/* Introduction */}
        <p style={styles.sectionContent}>
          At <strong>Frissly,</strong> we are committed to providing fresh,
          organic, and chemical-free products. Due to the{" "}
          <strong>made-to-order</strong> and <strong>perishable</strong> nature
          of our products, our return, refund, and cancellation policies comply
          with the{" "}
          <strong>Consumer Protection Act, 2019, FSSAI guidelines, </strong> and{" "}
          <strong>RBI regulations </strong>for digital transactions.
        </p>

        {/* Section 1: Returns Policy */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Returns Policy</h2>
          <p style={styles.sectionContent}>
            Since our products are prepared{" "}
            <strong>only after an order is placed,</strong> we generally{" "}
            <strong>do not accept returns.</strong> However, we will assist in
            resolving issues in the following cases:
          </p>

          {/* Subsection 1.1 */}
          <h3 style={styles.subSectionTitle}>1.1 Eligible Return Cases</h3>
          <p style={styles.sectionContent}>
            You may request a return or replacement if:
          </p>
          <ul style={styles.list}>
            <li style={styles.bulletPoint}>
              The product is <strong>damaged, expired, or spoiled </strong>
              at the time of delivery.
            </li>
            <li style={styles.bulletPoint}>
              You received the <strong>wrong product</strong> that does not
              match your order.
            </li>
            <li style={styles.bulletPoint}>
              The product was <strong>missing from your package</strong> despite
              being billed.
            </li>
          </ul>

          {/* Subsection 1.2 */}
          <h3 style={styles.subSectionTitle}>1.2 Non-Returnable Cases</h3>
          <p style={styles.sectionContent}>
            Returns are <strong>not accepted</strong> in the following cases:
          </p>
          <ul style={styles.list}>
            <li style={styles.bulletPoint}>
              Change of mind or personal dislike.
            </li>
            <li style={styles.bulletPoint}>
              Incorrect delivery address provided by the customer.
            </li>
            <li style={styles.bulletPoint}>
              Products opened, partially used, or tampered with after delivery.
            </li>
            <li style={styles.bulletPoint}>
              Delays due to external factors such as weather, strikes, or
              logistics issues.
            </li>
          </ul>

          {/* Subsection 1.3 */}
          <h3 style={styles.subSectionTitle}>1.3 Return Process</h3>
          <p style={styles.sectionContent}>
            • Customers must <strong>raise a request within 4 hours</strong> of
            delivery.
          </p>
          <p style={styles.sectionContent}>
            • A clear{" "}
            <strong>photo or video of the product and packaging</strong> must be
            shared via email or WhatsApp.
          </p>
          <p style={styles.sectionContent}>
            • Our <strong>support team will review</strong> the request and
            provide a resolution, which may include a replacement or refund.
          </p>
        </div>

        {/* Section 2: Refund Policy */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Refund Policy</h2>
          <p style={styles.sectionContent}>
            Refunds depend on{" "}
            <strong>bank and payment gateway guidelines.</strong> Our support
            team can assist in the refund process, but the final timelines are
            determined by your bank.
          </p>

          {/* Subsection 2.1 */}
          <h3 style={styles.subSectionTitle}>2.1 Eligible Refund Cases</h3>
          <p style={styles.sectionContent}>
            Refunds will be issued in the following cases:
          </p>
          <ul style={styles.list}>
            <li style={styles.bulletPoint}>
              If a <strong>damaged or incorrect product</strong> is reported
              within the return period.
            </li>
            <li style={styles.bulletPoint}>
              If an order is <strong>canceled before processing begins.</strong>
            </li>
            <li style={styles.bulletPoint}>
              If an order is{" "}
              <strong>not fulfilled due to unavailability.</strong>
            </li>
            <li style={styles.bulletPoint}>
              If a <strong>duplicate transaction</strong> occurs due to a
              technical issue.
            </li>
          </ul>

          {/* Subsection 2.2 */}
          <h3 style={styles.subSectionTitle}>2.2 Non-Refundable Cases</h3>
          <p style={styles.sectionContent}>
            Refunds will <strong>not</strong> be provided for:
          </p>
          <ul style={styles.list}>
            <li style={styles.bulletPoint}>
              Returns initiated after the <strong>4-hour window.</strong>
            </li>
            <li style={styles.bulletPoint}>
              Partially consumed or tampered products.
            </li>
            <li style={styles.bulletPoint}>
              Delivery delays due to external factors beyond Frissly’s control.
            </li>
          </ul>

          {/* Subsection 2.3 */}
          <h3 style={styles.subSectionTitle}>2.3 Refund Process</h3>
          <p style={styles.sectionContent}>
            • Once a refund is <strong>approved,</strong> it will be processed
            within <strong>5-7 business days.</strong>
          </p>
          <p style={styles.sectionContent}>
            <strong>• For online payments</strong> (UPI, Credit/Debit Cards, Net
            Banking, Wallets), refunds will be issued via the{" "}
            <strong>original payment method.</strong>
          </p>
          <p style={styles.sectionContent}>
            • We won’t allow <strong>Cash-on-Delivery (COD) orders,</strong> as
            we make products after order.
          </p>
          <p style={styles.sectionContent}>
            • If a refund is delayed beyond <strong>7 business days,</strong>{" "}
            customers can contact <strong>our support team,</strong> and we will
            assist in resolving the issue with the respective payment gateway or
            bank.
          </p>
        </div>

        {/* Section 3: Order Cancellations */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Order Cancellations</h2>
          <p style={styles.sectionContent}>
            Since our products are{" "}
            <strong>freshly made after an order is placed,</strong>{" "}
            cancellations are subject to the processing status:
          </p>
          <ul style={styles.list}>
            <li style={styles.bulletPoint}>
              <strong>Before processing starts:</strong> Free cancellation
              allowed.
            </li>
            <li style={styles.bulletPoint}>
              <strong>Once processing starts:</strong> Cancellation is not
              possible, as we begin making the product immediately after the
              order enters the processing stage.
            </li>
          </ul>
        </div>

        {/* Section 4: How to Raise a Request? */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. How to Raise a Request?</h2>
          <p style={styles.sectionContent}>
            For <strong>returns, refunds, or cancellations,</strong> contact our{" "}
            <strong>support team:</strong>
          </p>
          <ul style={styles.list}>
            <li style={styles.bulletPoint}>
              📧 <strong>Email:</strong>{" "}
              <a href="mailto:support@frissly.com" style={styles.email}>
                support@frissly.com
              </a>
            </li>
            <li style={styles.bulletPoint}>
              📞 <strong>Phone/WhatsApp:</strong> +91-9010703332
            </li>
            <li style={styles.bulletPoint}>
              📍 <strong>Address:</strong> Survey no 306, Divya Farms,
              Gundlapochampally, Gandimaisamma, Hyderabad, India
            </li>
          </ul>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  navbar: {
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  contentContainer: {
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
    textAlign: "center",
  },
  title: {
    fontSize: "24px",
    fontFamily: "Nunito, sans-serif",
    fontWeight: "bold",
    color: "#333333",
    marginBottom: "10px",
  },
  updatedDate: {
    fontSize: "16px",
    fontFamily: "Nunito, sans-serif",
    color: "#666666",
    marginBottom: "20px",
  },
  section: {
    marginBottom: "30px",
    textAlign: "left",
  },
  sectionTitle: {
    fontSize: "22px",
    fontFamily: "Nunito, sans-serif",
    fontWeight: "bold",
    color: "#333333",
    marginBottom: "15px",
    borderBottom: "1px solid #cccccc",
    paddingBottom: "10px",
    textAlign: "center",
  },
  subSectionTitle: {
    fontSize: "18px",
    fontFamily: "Nunito, sans-serif",
    fontWeight: "bold",
    color: "#333333",
    marginTop: "15px",
    marginBottom: "10px",
  },
  sectionContent: {
    fontSize: "16px",
    fontFamily: "Nunito, sans-serif",
    color: "#555555",
    marginBottom: "10px",
    lineHeight: "1.6",
  },
  list: {
    listStyleType: "none",
    paddingLeft: "0",
  },
  bulletPoint: {
    fontSize: "16px",
    fontFamily: "Nunito, sans-serif",
    color: "#555555",
    marginBottom: "8px",
    paddingLeft: "20px",
    position: "relative",
  },
  email: {
    color: "#007BFF",
    textDecoration: "underline",
    cursor: "pointer",
  },
  bold: {
    fontWeight: "bold",
    color: "#333333",
  },
};
